import { useEffect, useState } from "react";
import { useTerminMeldung, terminGeaendert, terminPlanen } from "@/lib/windowSync";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Calendar, Plus, Trash2, X, ChevronDown, ChevronUp, MapPin, Clock, Pencil,
  AlertTriangle, CheckCircle2, XCircle, HardHat,
} from "lucide-react";
import { TerminSendDialog } from "@/components/TerminSendDialog";
import { typBezeichnung } from "@/lib/terminTyp";
import { makeGoogleCalendarLink } from "@/lib/gcalLink";

const STATUS = {
  wartet_auf_go: { cls: "bg-amber-50 text-amber-800 border-amber-200", icon: AlertTriangle, label: "Wartet auf GO" },
  bestaetigt: { cls: "bg-blue-50 text-blue-800 border-blue-200", icon: Clock, label: "Bestätigt" },
  im_kalender: { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle2, label: "Im Kalender" },
  abgesagt: { cls: "bg-red-50 text-red-700 border-red-200", icon: XCircle, label: "Abgesagt" },
};


const fmtDateTime = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }); }
  catch { return s; }
};

/**
 * Wiederverwendbares Termine-Panel.
 * Datenmaske: liest und schreibt nur in module_termine, gefiltert auf Kunde oder Projekt.
 */
export const TerminePanel = ({ kunde_id = "", projekt_id = "", title = "Termine", defaultCollapsed = true, compact = true, onlyWithoutProjekt = false }) => {
  const [termine, setTermine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  // „+ Termin“ / Bearbeiten: eine zentrale Stelle – der Kalender (windowSync.terminPlanen); hier öffnet sich kein eigenes Formular
  const neuerTermin = () => terminPlanen({ modus: "termin", kunde_id, projekt_id });
  const [sendingTermin, setSendingTermin] = useState(null);
  const [mitarbeiter, setMitarbeiter] = useState([]);

  const params = {};
  if (kunde_id) params.kunde_id = kunde_id;
  if (projekt_id) params.projekt_id = projekt_id;

  const load = async () => {
    if (!kunde_id && !projekt_id) return;
    setLoading(true);
    try {
      const [r, m] = await Promise.all([
        api.get("/module-termine", { params }),
        mitarbeiter.length ? Promise.resolve({ data: mitarbeiter }) : api.get("/module-aufgaben/mitarbeiter").catch(() => ({ data: [] })),
      ]);
      setTermine(Array.isArray(r.data) ? (onlyWithoutProjekt ? r.data.filter(t => !t.projekt_id) : r.data) : []);
      if (!mitarbeiter.length) setMitarbeiter(Array.isArray(m.data) ? m.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Termine konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [kunde_id, projekt_id]);  // eslint-disable-line
  useTerminMeldung(load);   // Planung im zweiten Fenster gespeichert -> Liste neu laden

  const onGo = async (t) => {
    if (!window.confirm(`Termin "${t.titel}" am ${fmtDateTime(t.start)} bestätigen (GO)?`)) return;
    try {
      await api.patch(`/module-termine/${t.id}/go`); terminGeaendert();
      toast.success("Termin bestätigt – jetzt Empfänger wählen und ICS senden");
      setSendingTermin(t);  // Empfänger-Dialog öffnen
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "GO fehlgeschlagen"); }
  };

  const onCancel = async (t) => {
    const grund = window.prompt(`Grund für Absage von "${t.titel}"?`, "");
    if (grund === null) return;
    try {
      await api.patch(`/module-termine/${t.id}/cancel`, { status: "abgesagt", grund }); terminGeaendert();
      toast.success("Termin abgesagt"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Absage fehlgeschlagen"); }
  };

  const onDelete = async (t) => {
    if (!window.confirm(`Termin "${t.titel}" löschen?`)) return;
    try {
      await api.delete(`/module-termine/${t.id}`); terminGeaendert();
      toast.success("Termin gelöscht"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Löschen fehlgeschlagen"); }
  };

  const offenCount = termine.filter(t => t.status === "wartet_auf_go" || t.status === "bestaetigt").length;

  return (
    <div className="border rounded-md bg-background" data-testid={`termine-panel-${kunde_id || projekt_id}`}>
      <button
        type="button"
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between p-3 hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-primary" />
          <span className="font-semibold text-sm">{title}</span>
          {termine.length > 0 && (
            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
              {offenCount > 0 ? `${offenCount} offen / ` : ""}{termine.length} gesamt
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!collapsed && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); neuerTermin(); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); neuerTermin(); } }}
              className="text-xs px-2 py-1 bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 flex items-center gap-1 cursor-pointer"
              data-testid={`btn-termin-add-${kunde_id || projekt_id}`}
            >
              <Plus className="w-3 h-3" /> Neu
            </span>
          )}
          {collapsed ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronUp className="w-4 h-4 text-muted-foreground" />}
        </div>
      </button>

      {!collapsed && (
        <div className="border-t p-3 space-y-2">
          {loading ? (
            <p className="text-xs text-muted-foreground text-center py-3">Lade…</p>
          ) : termine.length === 0 ? (
            <div className="text-xs text-muted-foreground text-center py-4">
              Keine Termine für {kunde_id ? "diesen Kunden" : "dieses Projekt"}.
              <button onClick={neuerTermin} className="block mx-auto mt-2 text-primary hover:underline">
                + Ersten Termin anlegen
              </button>
            </div>
          ) : (
            termine.map(t => {
              const sty = STATUS[t.status] || STATUS.wartet_auf_go;
              const Icon = sty.icon;
              const monteurName = mitarbeiter.find(m => m.username === t.monteur_username)?.anzeige_name;
              return (
                <div
                  key={t.id}
                  className="border rounded-sm p-2"
                  data-testid={`panel-termin-${t.id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`p-1 rounded-sm border ${sty.cls} flex-shrink-0`}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{t.titel}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${sty.cls}`}>{sty.label}</span>
                        <span className="text-[10px] text-muted-foreground">{typBezeichnung(t.typ)}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDateTime(t.start)}</span>
                        {t.ort && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {t.ort}</span>}
                        {monteurName && <span className="flex items-center gap-1"><HardHat className="w-3 h-3" /> {monteurName}</span>}
                      </div>
                      {!compact && t.beschreibung && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.beschreibung}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {t.status === "wartet_auf_go" && (
                        <button
                          onClick={() => onGo(t)}
                          className="text-xs px-2 py-0.5 bg-emerald-500 text-white rounded-sm hover:bg-emerald-600 font-bold"
                          data-testid={`panel-termin-go-${t.id}`}
                        >
                          GO
                        </button>
                      )}
                      {(t.status === "bestaetigt" || t.status === "im_kalender") && (
                        <button
                          onClick={() => setSendingTermin(t)}
                          className="text-xs px-2 py-0.5 border border-blue-300 text-blue-700 rounded-sm hover:bg-blue-50"
                          title="ICS-Mail (erneut) senden"
                          data-testid={`panel-termin-send-${t.id}`}
                        >
                          📧
                        </button>
                      )}
                      <a
                        href={makeGoogleCalendarLink(t)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs px-2 py-0.5 border border-emerald-300 text-emerald-700 rounded-sm hover:bg-emerald-50"
                        title="In Google Kalender eintragen"
                        data-testid={`panel-termin-gcal-${t.id}`}
                      >
                        🗓️
                      </a>
                      <button
                        onClick={() => terminPlanen({ termin_id: t.id })}
                        className="p-1 text-muted-foreground hover:bg-muted rounded-sm"
                        title="Bearbeiten"
                        data-testid={`panel-termin-edit-${t.id}`}
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      {t.status !== "abgesagt" && t.status !== "im_kalender" && (
                        <button
                          onClick={() => onCancel(t)}
                          className="text-xs px-1.5 py-0.5 border border-red-200 text-red-700 rounded-sm hover:bg-red-50"
                          title="Absagen"
                        >
                          ✕
                        </button>
                      )}
                      <button
                        onClick={() => onDelete(t)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded-sm"
                        title="Löschen"
                        data-testid={`panel-termin-delete-${t.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}


      {sendingTermin && (
        <TerminSendDialog
          termin_id={sendingTermin.id}
          onClose={() => setSendingTermin(null)}
          onSent={() => { setSendingTermin(null); load(); }}
        />
      )}
    </div>
  );
};


export default TerminePanel;
